import React from "react";
import { createStandaloneToast, Box, Link, Text } from "@chakra-ui/react";
import { auth, db } from "../firebase";
import firebase from "firebase/app";
import {
  SUCCESS_TOAST_DURATION,
  USERS_COLLECTION_NAME,
  ERROR_TOAST_DURATION,
} from "./constants";
import debug from "debug";
import { Link as ReachLink } from "@reach/router";
import { User } from "./types";
import { toCapital } from "./utils";

/**
 * Single function to handle social sign-up / login
 */
export const socialSignIn = async (strategy: "facebook" | "google") => {
  const toast = createStandaloneToast();

  // Decide the apt firebase auth provider
  const providerList = {
    google: new firebase.auth.GoogleAuthProvider(),
    facebook: new firebase.auth.FacebookAuthProvider(),
  };
  const provider = providerList[strategy];

  try {
    const result = await auth.signInWithPopup(provider);

    // Throw right away, if the user is not found!
    if (!result.user) {
      throw new Error("Error in Sign Up!");
    }

    debug("googleSignIn success")(result);
    toast({
      title: "Inicio de sesión exitoso",
      description: `Bienvenido a Pulpo`,
      status: "success",
      duration: SUCCESS_TOAST_DURATION,
      isClosable: true,
    });
    return {
      user: result.user,
      additionUserInfo: result.additionalUserInfo || null,
    };
  } catch (error) {
    debug("googleSignIn")(error.code);
    console.log(error);
    if (error.code === "auth/account-exists-with-different-credential") {
      const { credential: pendingCredentials, email } = error;
      auth
        .fetchSignInMethodsForEmail(email)
        .then((methods) => {
          console.log(methods[0]);
          if (methods[0] !== "email") {
            toast({
              title: `You already have an account with ${methods[0]}`,
              description: `Redirecting to ${methods[0]}...`,
              status: "warning",
              duration: ERROR_TOAST_DURATION,
              isClosable: false,
            });
            const newProvider =
              providerList[methods[0] === "google.com" ? "google" : "facebook"];
            auth.signInWithPopup(newProvider).then(function (result) {
              if (!result.user) {
                throw new Error("User not found while linking");
              }
              result.user
                .linkWithCredential(pendingCredentials)
                .then(function () {
                  return {
                    user: result.user,
                    additionUserInfo: result.additionalUserInfo || null,
                  };
                });
            });
          } else {
            throw new Error("User has previously signed up using email");
          }
        })
        .catch((e) => {
          toast({
            title: "An error occurred.",
            description: "Unable to sign in the user.",
            status: "error",
            duration: ERROR_TOAST_DURATION,
            isClosable: true,
          });
          return null;
        });
    } else {
      toast({
        title: "An error occurred.",
        description: "Unable to sign in the user.",
        status: "error",
        duration: ERROR_TOAST_DURATION,
        isClosable: true,
      });
      return null;
    }
  }
};

/**
 * Function to login user using email / password
 */
export const emailLogin = async (email: string, password: string) => {
  const toast = createStandaloneToast();

  try {
    const result = await auth.signInWithEmailAndPassword(email, password);

    // Throw right away, if the user is not found!
    if (!result.user) {
      throw new Error("Error in Sign Up!");
    }

    debug("emailLogin success")(result);
    toast({
      title: "Inicio de sesión exitoso",
      description: `Bienvenido a Pulpo`,
      status: "success",
      duration: SUCCESS_TOAST_DURATION,
      isClosable: true,
    });
    return {
      user: result.user,
      additionUserInfo: result.additionalUserInfo || null,
    };
  } catch (error) {
    debug("emailLogin")(error.code);
    if (
      [
        "auth/invalid-email",
        "auth/user-disabled",
        "auth/user-not-found",
      ].includes(error.code)
    ) {
      toast({
        title: "User not found!",
        description: (
          <Box>
            <Text>
              User with {email} does not exist.{" "}
              <Link as={ReachLink} to="/register">
                Please register.
              </Link>
            </Text>
          </Box>
        ),
        status: "error",
        duration: ERROR_TOAST_DURATION,
        isClosable: true,
      });
    } else {
      toast({
        title: "Invalid credentials",
        description: `Please check your email and password and try again!`,
        status: "error",
        duration: ERROR_TOAST_DURATION,
        isClosable: true,
      });
    }
    return null;
  }
};

type EmailSignUpUser = {
  email: string;
  password: string;
  profile: {
    displayName: string;
    photoURL: string;
  };
};
/**
 * Function to create a new user using email / password
 */
export const emailSignUp = async (user: EmailSignUpUser) => {
  const { email, password, profile } = user;
  const toast = createStandaloneToast();

  try {
    const result = await auth.createUserWithEmailAndPassword(email, password);

    // Throw right away, if the user is not found!
    if (!result.user) {
      throw new Error("Error in Sign Up!");
    }

    debug("emailSignUp success")(result);
    toast({
      title: "Registro exitoso",
      description: `Bienvenid@ a Pulpo`,
      status: "success",
      duration: SUCCESS_TOAST_DURATION,
      isClosable: true,
    });
    await result.user.updateProfile(profile);
    return {
      user: result.user,
      additionUserInfo: result.additionalUserInfo || null,
    };
  } catch (error) {
    debug("emailSignUp")(error.code);
    if (error.code === "auth/email-already-in-use") {
      toast({
        title: "Error in Sign Up!",
        description: (
          <Box>
            <Text>
              User with {email} already exists.{" "}
              <Link as={ReachLink} to="/login">
                Try login
              </Link>
            </Text>
          </Box>
        ),
        status: "error",
        duration: ERROR_TOAST_DURATION,
        isClosable: true,
      });
    }

    if (error.code === "auth/invalid-email") {
      toast({
        title: "Error in Sign Up!",
        description: "Invalid Email",
        status: "error",
        duration: ERROR_TOAST_DURATION,
        isClosable: true,
      });
    }

    if (error.code === "auth/operation-not-allowed") {
      toast({
        title: "Error in Sign Up!",
        description: "Please try again!",
        status: "error",
        duration: ERROR_TOAST_DURATION,
        isClosable: true,
      });
    }

    if (error.code === "auth/weak-password") {
      toast({
        title: "Error in Sign Up!",
        description: "Please try a stronger password",
        status: "error",
        duration: ERROR_TOAST_DURATION,
        isClosable: true,
      });
    }

    return null;
  }
};

/**
 * Function to send password reset email
 */
export const sendPasswordResetMail = async (email: string) => {
  const toast = createStandaloneToast();

  try {
    await auth.sendPasswordResetEmail(email);
    toast({
      title: "Email Sent",
      description:
        "If an account with this email exists, you should receive a password reset email. Please follow the steps mentioned in that email to reset your password",
      status: "success",
      duration: SUCCESS_TOAST_DURATION,
      isClosable: true,
    });
  } catch (error) {
    debug("sendPasswordResetMail")(error.code || error);
    toast({
      title: "Oops! Some error occurred",
      description: "Please try again after sometime",
      status: "error",
      duration: ERROR_TOAST_DURATION,
      isClosable: true,
    });
  }
};

export const createUser = async (
  user: Omit<
    User,
    | "uid"
    | "surname"
    | "paid"
    | "stripe_customer"
    | "platform_notification_subscriptions"
  >
) => {
  const toast = createStandaloneToast();

  try {
    const userRef = db.collection(USERS_COLLECTION_NAME).doc();
    await userRef.set({
      uid: userRef.id,
      ...user,
      name: toCapital(user.name),
      paid: false,
      platform_notification_subscriptions: [],
    });
    return userRef.id;
  } catch (error) {
    debug("createUser")(error.code || error);
    toast({
      title: "Some error occurred!",
      description: "Please try again",
      status: "error",
      duration: ERROR_TOAST_DURATION,
      isClosable: true,
    });
    return null;
  }
};

export const userExists = async (email: string) => {
  const toast = createStandaloneToast();

  try {
    const user = await db
      .collection(USERS_COLLECTION_NAME)
      .where("email", "==", email)
      .get();
    return !user.empty;
  } catch (error) {
    debug("userExists")(error.code || error);
    toast({
      title: "Some error occurred!",
      description: "Please try again",
      status: "error",
      duration: ERROR_TOAST_DURATION,
      isClosable: true,
    });
    return false;
  }
};

export const sendSMS = async (uid: string, phone: string, token: string) => {
  const toast = createStandaloneToast();

  try {
    const sendSMSFn = firebase.functions().httpsCallable("sendSMS");
    const response = await sendSMSFn({
      uid,
      phone,
      token,
    });
    if (response.data.success) {
      toast({
        title: "¡SMS enviado!",
        status: "success",
        duration: SUCCESS_TOAST_DURATION,
        isClosable: true,
      });
      return {
        status: true,
      };
    } else {
      throw new Error();
    }
  } catch (error) {
    toast({
      title: "Error al enviar SMS",
      description: "Reintenta o prueba más tarde.",
      status: "error",
      duration: ERROR_TOAST_DURATION,
      isClosable: true,
    });
    return {
      result: error,
      status: false,
    };
  }
};

export const verifyOTP = async (uid: string, verificationCode: string) => {
  const toast = createStandaloneToast();

  try {
    const verifySMS = firebase.functions().httpsCallable("verifySMS");
    const response = await verifySMS({
      uid,
      verificationCode,
    });
    if (response.data.success) {
      toast({
        title: "Móvil verificado correctamente",
        status: "success",
        duration: SUCCESS_TOAST_DURATION,
        isClosable: true,
      });
      return {
        status: true,
      };
    } else {
      throw new Error();
    }
  } catch (error) {
    toast({
      title: "Error al verificar código",
      description: "Comprueba que el código sea correcto o reintenta.",
      duration: ERROR_TOAST_DURATION,
      isClosable: true,
    });
    return {
      result: error,
      status: false,
    };
  }
};

export const signOut = () => {
  try {
    localStorage.clear();
  } catch (e) {
    console.error(`Error in clearing local storage: ${e}`);
  }
  return auth.signOut();
};
